import { FC, useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DropdownSelector2, TimeRangePicker } from '../../../ui'
import moment, { Moment } from 'moment'
import useRequest from '../../../hooks/useRequest'
import { selectDictionariesOptions } from '../../../../store/dictionaries'
import { postGraphData } from './config'
import { useAppSelector } from '../../../../store/hooks'
import Graph from '../../../ui/Graph/Graph'
import useWindowWidth from '../../../hooks/useWindowWidth'
import style from './style.m.less'

const GraphFrame: FC = () => {
    const { t, i18n } = useTranslation()
    const windowWidth = useWindowWidth()
    const isMobile = windowWidth <= 1000
    const { fetch, result } = useRequest(postGraphData)
    const [type, setType] = useState(0)
    const [range, setRange] = useState<[Moment, Moment] | null>()
    const { renewableEnergyTypesOptions } = useAppSelector(selectDictionariesOptions) ?? {}

    const locale = useMemo(() => {
        const lang = i18n.language.toLowerCase()
        return lang.includes('ru') ? 'ru' : 'en'
    }, [i18n.language])

    useEffect(() => {
        moment.locale(locale)
    }, [locale])

    if (!range) {
        let startDate: Moment
        if (isMobile) {
            startDate = moment().startOf('month').subtract(6, 'months')
        } else if (windowWidth <= 1300) {
            startDate = moment().startOf('month').subtract(12, 'months')
        } else {
            startDate = moment([2024, 1]).startOf('month')
        }
        setRange([
            startDate,
            moment().startOf('month').subtract(1, 'months'),
        ])
    }

    const rangeHandler = (r: [Moment, Moment]) => {
        if (!r) {
            setRange(null)
            return
        }
        setRange([moment(r[0]).startOf('month'), moment(r[1]).startOf('month')])
    }

    const realRenewableEnergyTypesOptions = useMemo(
        () =>
            renewableEnergyTypesOptions
                ? [
                      {
                          id: 0,
                          value: 0,
                          text: t('main.allRenewableEnergy'),
                          label: t('main.allRenewableEnergy'),
                      },
                      ...renewableEnergyTypesOptions,
                  ]
                : [
                      {
                          id: 0,
                          value: 0,
                          text: t('main.allRenewableEnergy'),
                          label: t('main.allRenewableEnergy'),
                      },
                  ],
        [renewableEnergyTypesOptions, t],
    )

    const fetchData = () => {
        const types = {
            renewable_energy_type_id: type,
        }
        const body = range
            ? {
                  ...{
                      event_date_from: moment(range[0]).startOf('day'),
                      event_date_to: moment(range[1]).endOf('day'),
                  },
                  ...types,
              }
            : { ...types }
        fetch({
            body,
            onSuccess: (result: any) => {
                let b = true
                for (let i = 0; i < result.stats.length; i++) {
                    if (result.stats[i].period === range[1].format('YYYY-MM-DD')) {
                        b = false
                        break
                    }
                }
                if (b) {
                    range[1].subtract(1, 'months')
                }
            },
        })
    }

    useEffect(() => {
        fetchData()
    }, [type, range])

    return (
        <div className={style.graphFrameMain}>
            <div className={style.headerRow}>
                <div>
                    <span className={style.headerPrimary}>{t('main.graphVolume')}</span>
                    <span className={style.headerSecondary}>({t('main.scale')})</span>
                </div>
                <div className={style.filters}>
                    <DropdownSelector2
                        customStyle={{ width: '223px' }}
                        options={realRenewableEnergyTypesOptions}
                        onChange={(value) => setType(value)}
                        currentLang={locale}
                        value={type}
                    />
                    <TimeRangePicker
                        style={{ width: '223px' }}
                        onlyBeforeDateAndToday={moment()}
                        picker="month"
                        value={range}
                        format={'MMMM YY'}
                        onChange={(value) => rangeHandler(value)}
                    />
                </div>
            </div>
            <div className={style.graph}>
                <Graph stats={result?.stats} period={range} smallScreen={isMobile} />
            </div>
        </div>
    )
}

export default GraphFrame
